import { muscleSchema, type Muscle } from '@cadencia/contracts';

/** Vocabulario de musculos y patrones. Es la base del reparto de volumen. */

/**
 * La lista se declara en `@cadencia/contracts` desde que las prioridades se
 * persisten: las CLAVES de `muscle_priorities` son estos nombres, asi que
 * forman parte del contrato con la base de datos, igual que `equipmentKind`.
 * Aqui se reexporta para que el resto del dominio no cambie de import y para
 * que no haya dos listas que puedan divergir en silencio.
 */
export const MUSCLES = muscleSchema.options;

export type { Muscle };

export const MUSCLE_LABELS: Record<Muscle, string> = {
  pectoral: 'Pectoral',
  dorsal: 'Dorsal',
  trapecio: 'Trapecio',
  romboides: 'Romboides',
  deltoide_anterior: 'Hombro anterior',
  deltoide_lateral: 'Hombro lateral',
  deltoide_posterior: 'Hombro posterior',
  biceps: 'Bíceps',
  triceps: 'Tríceps',
  antebrazo: 'Antebrazo',
  cuadriceps: 'Cuádriceps',
  isquios: 'Isquiosurales',
  gluteo: 'Glúteo',
  aductores: 'Aductores',
  gemelo: 'Gemelo',
  soleo: 'Sóleo',
  core: 'Core',
  lumbar: 'Lumbar',
};

export const MOVEMENT_PATTERNS = [
  'horizontal_push',
  'vertical_push',
  'horizontal_pull',
  'vertical_pull',
  'squat',
  'hinge',
  'lunge',
  'isolation_elbow_flex',
  'isolation_elbow_ext',
  'isolation_knee_flex',
  'isolation_knee_ext',
  'isolation_shoulder_abd',
  'isolation_chest_fly',
  'isolation_calf',
  // Abducción, aducción y extensión de cadera aisladas (biblioteca v2).
  'isolation_hip',
  'core',
] as const;

export type MovementPattern = (typeof MOVEMENT_PATTERNS)[number];

/**
 * Region del mapa corporal a la que pertenece cada musculo. Se usa para cruzar
 * las agujetas declaradas en el check-in con los musculos de la sesion.
 */
export const BODY_REGIONS = [
  'chest',
  'upper_back',
  'lower_back',
  'shoulders_front',
  'shoulders_side',
  'shoulders_rear',
  'biceps',
  'triceps',
  'forearms',
  'abs',
  'quads',
  'hamstrings',
  'glutes',
  'calves',
  'hip',
  'knee',
  'ankle',
  'elbow',
  'wrist',
  'neck',
] as const;

export type BodyRegion = (typeof BODY_REGIONS)[number];

export const BODY_REGION_LABELS: Record<BodyRegion, string> = {
  chest: 'Pecho',
  upper_back: 'Espalda alta',
  lower_back: 'Zona lumbar',
  shoulders_front: 'Hombro anterior',
  shoulders_side: 'Hombro lateral',
  shoulders_rear: 'Hombro posterior',
  biceps: 'Bíceps',
  triceps: 'Tríceps',
  forearms: 'Antebrazos',
  abs: 'Abdomen',
  quads: 'Cuádriceps',
  hamstrings: 'Isquiosurales',
  glutes: 'Glúteos',
  calves: 'Gemelos',
  hip: 'Cadera',
  knee: 'Rodilla',
  ankle: 'Tobillo',
  elbow: 'Codo',
  wrist: 'Muñeca',
  neck: 'Cuello',
};

const MUSCLE_TO_REGION: Record<Muscle, BodyRegion> = {
  pectoral: 'chest',
  dorsal: 'upper_back',
  trapecio: 'upper_back',
  romboides: 'upper_back',
  deltoide_anterior: 'shoulders_front',
  deltoide_lateral: 'shoulders_side',
  deltoide_posterior: 'shoulders_rear',
  biceps: 'biceps',
  triceps: 'triceps',
  antebrazo: 'forearms',
  cuadriceps: 'quads',
  isquios: 'hamstrings',
  gluteo: 'glutes',
  aductores: 'hip',
  gemelo: 'calves',
  soleo: 'calves',
  core: 'abs',
  lumbar: 'lower_back',
};

export function regionForMuscle(muscle: Muscle): BodyRegion {
  return MUSCLE_TO_REGION[muscle];
}

/** Agujetas maximas declaradas en las regiones que trabaja un ejercicio. */
export function sorenessForMuscles(
  muscles: readonly Muscle[],
  soreness: readonly { region: BodyRegion; intensity: number }[],
): number | null {
  const regions = new Set(muscles.map(regionForMuscle));
  const relevant = soreness.filter((entry) => regions.has(entry.region));
  if (relevant.length === 0) return null;
  return relevant.reduce((max, entry) => Math.max(max, entry.intensity), 0);
}
